import { useEffect, useState, useCallback } from "react";
import { Satellite, Loader2, ShieldX, PlugZap, Save, KeyRound, CheckCircle2, XCircle } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ESTADO_BADGE = {
  mock: { label: "Modo demo (mock)", cls: "bg-slate-100 text-slate-600" },
  sandbox: { label: "Conectado (sandbox)", cls: "bg-sky-100 text-sky-700" },
  produccion: { label: "Conectado (producción)", cls: "bg-emerald-100 text-emerald-700" },
  "no configurado": { label: "Pendiente de credenciales", cls: "bg-amber-100 text-amber-700" },
};

function EstadoBadge({ estado }) {
  const m = ESTADO_BADGE[estado] || ESTADO_BADGE["no configurado"];
  return (
    <span data-testid="qeq-estado-badge" className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

export default function ProveedorListas() {
  const { user } = useAuth();
  const [vista, setVista] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [clientId, setClientId] = useState("");
  const [secretId, setSecretId] = useState("");
  const [username, setUsername] = useState("");
  const [urlSandbox, setUrlSandbox] = useState("");
  const [urlProduccion, setUrlProduccion] = useState("");
  const [modo, setModo] = useState("mock");
  const [saving, setSaving] = useState(false);
  const [probing, setProbing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, e] = await Promise.all([
        api.get("/admin/proveedor-listas/credencial"),
        api.get("/admin/proveedor-listas/eventos"),
      ]);
      setVista(c.data);
      setEventos(e.data);
      setModo(c.data.modo || "mock");
      setUrlSandbox(c.data.url_base_sandbox || "");
      setUrlProduccion(c.data.url_base_produccion || "");
    } catch (err) { setError(true); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user && user.role !== "super_admin") { setError(true); setLoading(false); return; }
    if (user) load();
  }, [user, load]);

  const guardar = async () => {
    setSaving(true);
    try {
      const body = {
        modo,
        url_base_sandbox: urlSandbox,
        url_base_produccion: urlProduccion,
        secretos: {},
      };
      if (clientId.trim()) body.secretos.client_id = clientId.trim();
      if (secretId.trim()) body.secretos.secret_id = secretId.trim();
      if (username.trim()) body.secretos.username = username.trim();
      const { data } = await api.put("/admin/proveedor-listas/credencial", body);
      setVista(data);
      setClientId(""); setSecretId(""); setUsername("");
      toast.success("Credencial guardada cifrada. Token cacheado invalidado.");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const probar = async () => {
    setProbing(true);
    try {
      const { data } = await api.post("/admin/proveedor-listas/probar");
      if (data.ok) toast.success(data.mensaje);
      else toast.error(data.mensaje);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setProbing(false); }
  };

  if (error)
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-10 text-center" data-testid="qeq-forbidden">
        <ShieldX className="h-10 w-10 text-red-400" />
        <p className="font-heading text-lg font-semibold text-slate-700">Acceso restringido</p>
        <p className="max-w-md text-sm text-slate-500">
          La credencial madre del proveedor de listas solo puede gestionarla el Super Administrador de la plataforma.
        </p>
      </div>
    );

  if (loading || !vista)
    return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  return (
    <div data-testid="qeq-page">
      <PageHeader
        title="Proveedor de Listas — Quién es Quién"
        subtitle="Credencial madre a nivel plataforma (Q-Detect) · cifrada con Fernet · solo Super Administrador"
      />
      <div className="grid gap-6 p-6 md:p-8 lg:grid-cols-5">
        {/* Estado */}
        <div className="lg:col-span-2">
          <div data-testid="qeq-estado-card" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <Satellite className="h-5 w-5 text-primary" />
              <h2 className="font-heading text-base font-semibold text-slate-800">Estado de la credencial</h2>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Estado</span>
                <EstadoBadge estado={vista.estado} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Modo</span>
                <span data-testid="qeq-modo-actual" className="font-medium text-slate-700">{vista.modo}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">client_id</span>
                <span data-testid="qeq-client-id-mask" className="font-mono text-slate-700">{vista.client_id_mask || "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">secret_id</span>
                <span data-testid="qeq-secret-id-mask" className="font-mono text-slate-700">{vista.secret_id_mask || "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">username (/api/find)</span>
                <span data-testid="qeq-username-mask" className="font-mono text-slate-700">
                  {vista.username_mask || <span className="text-amber-600">Sin configurar</span>}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Token cacheado</span>
                <span data-testid="qeq-token-cache" className="inline-flex items-center gap-1 font-medium">
                  {vista.token_cacheado
                    ? <><CheckCircle2 className="h-4 w-4 text-emerald-500" /><span className="text-emerald-600">Vigente (cifrado)</span></>
                    : <><XCircle className="h-4 w-4 text-slate-400" /><span className="text-slate-500">Sin token</span></>}
                </span>
              </div>
              {vista.token_cacheado && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Expira</span>
                  <span className="text-slate-600">{fdate(vista.token_expira)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Última prueba</span>
                <span data-testid="qeq-ultima-prueba" className="text-slate-600">
                  {vista.ultima_prueba ? `${fdate(vista.ultima_prueba)} · ${vista.ultima_prueba_ok ? "OK" : "Falló"}` : "—"}
                </span>
              </div>
              {vista.ultimo_resultado && (
                <p data-testid="qeq-ultimo-resultado" className="rounded-md bg-slate-50 p-2 text-xs text-slate-500">{vista.ultimo_resultado}</p>
              )}
            </div>
            <Button data-testid="qeq-probar-btn" onClick={probar} disabled={probing}
                    className="mt-5 w-full gap-2 bg-accent hover:bg-forest-700">
              {probing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
              Probar conexión
            </Button>
            <p className="mt-3 text-xs text-slate-400">
              La prueba solicita el token a <span className="font-mono">GET /api/token</span> del proveedor (no consume saldo).
              El token nunca se muestra ni se registra en bitácora.
            </p>
          </div>
        </div>

        {/* Formulario */}
        <div className="lg:col-span-3">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              <h2 className="font-heading text-base font-semibold text-slate-800">Configurar credencial madre</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-slate-500">client_id (se envía en la URL)</Label>
                <Input data-testid="qeq-client-id-input" value={clientId} onChange={(e) => setClientId(e.target.value)}
                       placeholder={vista.client_id_mask ? `Guardado: ${vista.client_id_mask}` : "client_id del proveedor"} />
              </div>
              <div>
                <Label className="text-xs text-slate-500">secret_id (Authorization: Bearer)</Label>
                <Input data-testid="qeq-secret-id-input" type="password" value={secretId} onChange={(e) => setSecretId(e.target.value)}
                       placeholder={vista.secret_id_mask ? `Guardado: ${vista.secret_id_mask}` : "secret_id del proveedor"} />
              </div>
              <div>
                <Label className="text-xs text-slate-500">username (se envía en /api/find — distinto del client_id)</Label>
                <Input data-testid="qeq-username-input" value={username} onChange={(e) => setUsername(e.target.value)}
                       placeholder={vista.username_mask ? `Guardado: ${vista.username_mask}` : "usuario asignado por el proveedor (p. ej. Servicios6)"} />
              </div>
              <div>
                <Label className="text-xs text-slate-500">URL base sandbox (preproducción)</Label>
                <Input data-testid="qeq-url-sandbox-input" value={urlSandbox} onChange={(e) => setUrlSandbox(e.target.value)}
                       placeholder="https://app.q-detect.com" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">URL base producción (se confirmará después)</Label>
                <Input data-testid="qeq-url-produccion-input" value={urlProduccion} onChange={(e) => setUrlProduccion(e.target.value)}
                       placeholder="Pendiente de confirmar con el proveedor" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Modo</Label>
                <Select value={modo} onValueChange={setModo}>
                  <SelectTrigger data-testid="qeq-modo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mock">Modo demo (mock)</SelectItem>
                    <SelectItem value="sandbox">Sandbox (preproducción)</SelectItem>
                    <SelectItem value="produccion">Producción</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button data-testid="qeq-guardar-btn" onClick={guardar} disabled={saving} className="w-full gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Guardar credencial
                </Button>
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-400">
              Los secretos se cifran en reposo (Fernet) y solo se muestran los últimos 4 caracteres. Deja un campo vacío
              para conservar el valor guardado. Guardar invalida el token cacheado. Cuenta madre única a nivel plataforma:
              ninguna entidad tiene acceso a esta credencial. La consulta de búsqueda (que consume saldo) se conectará en
              un bloque posterior.
            </p>
          </div>

          {/* Bitácora */}
          <div className="mt-6">
            <h3 className="mb-3 font-heading text-sm font-semibold text-slate-700">Bitácora del proveedor (sin PII)</h3>
            <DataTable
              testid="qeq-eventos-table"
              rows={eventos}
              emptyLabel="Sin eventos registrados"
              columns={[
                { key: "created_at", label: "Fecha", kind: "date" },
                { key: "accion", label: "Acción" },
                { key: "resultado", label: "Resultado", kind: "badge" },
                { key: "modo", label: "Modo" },
                { key: "mensaje", label: "Mensaje" },
                { key: "usuario", label: "Usuario" },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
